import { useEffect, useState, useCallback } from 'react'
import {
  apiAgentGetDashboard, apiAgentGetProfile, apiAgentUpdateSettings, apiAgentSetWalletApiKey,
  ApiRequestError,
} from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertCircle, Wrench, KeyRound, Eye, EyeOff } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useT } from '@/contexts/language'

// ─── Wallet API key ───────────────────────────────────────────────────────────
//
// Self-contained: its own loading/error/success state, so a failed save here never
// blanks out the maintenance-mode card's status message above it (and vice versa).

function WalletApiKeyCard() {
  const t = useT()
  const [maskedCurrent, setMaskedCurrent] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [value, setValue] = useState('')
  const [show, setShow] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    apiAgentGetProfile()
      .then((profile) => { if (!cancelled) setMaskedCurrent(profile.walletApiKeyMasked || null) })
      .catch(() => { /* the card degrades to "not set" rather than blocking the page */ })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  async function handleSave() {
    setSaving(true)
    setError(null)
    setSuccessMsg(null)
    try {
      const result = await apiAgentSetWalletApiKey(value)
      setMaskedCurrent(result.walletApiKeyMasked)
      setValue('')
      setShow(false)
      setSuccessMsg(t.agentSettings.walletApiKeySaveSuccess)
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.agentSettings.walletApiKeySaveError)
    } finally {
      setSaving(false)
    }
  }

  const tooShort = value.length > 0 && value.length < 16

  return (
    <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden shadow-[var(--shadow-sm)] mt-4">
      <div className="px-4 py-2.5 bg-secondary flex items-center gap-2">
        <KeyRound className="h-3.5 w-3.5 text-secondary-foreground/60" aria-hidden />
        <span className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
          {t.agentSettings.walletApiKeyTitle}
        </span>
      </div>

      <div className="p-4">
        <p className="font-mono text-xs text-muted-foreground mb-4">
          {t.agentSettings.walletApiKeyDesc}
        </p>

        {error && (
          <div className="flex items-center gap-2 p-3 border border-destructive/40 rounded-[var(--radius)] bg-card mb-4" role="alert">
            <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
            <p className="font-mono text-xs text-destructive">{error}</p>
          </div>
        )}

        {successMsg && (
          <div className="flex items-center gap-2 p-3 border border-success/40 rounded-[var(--radius)] bg-card mb-4">
            <span className="h-4 w-4 rounded-full bg-success/20 flex items-center justify-center shrink-0">
              <span className="h-2 w-2 rounded-full bg-success" />
            </span>
            <p className="font-mono text-xs text-success">{successMsg}</p>
          </div>
        )}

        {loading ? (
          <Skeleton className="h-9 w-full mb-4" />
        ) : (
          <code className="block font-mono text-xs bg-secondary/40 border border-border rounded-[var(--radius-sm)] px-3 py-2 text-foreground break-all mb-4">
            {maskedCurrent ?? t.agentSettings.walletApiKeyCurrentNotSet}
          </code>
        )}

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="walletApiKey" className="font-mono text-xs">
            {t.agentSettings.walletApiKeyLabel}
          </Label>
          <div className="relative">
            <Input
              id="walletApiKey"
              type={show ? 'text' : 'password'}
              placeholder={t.agentSettings.walletApiKeyPlaceholder}
              className="font-mono text-sm pr-9"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              aria-invalid={tooShort}
            />
            <button
              type="button"
              onClick={() => setShow((v) => !v)}
              className={cn(
                'absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm',
              )}
              aria-label={show ? 'Hide key' : 'Show key'}
            >
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {tooShort && (
            <p className="font-mono text-[10px] text-destructive">{t.agentSettings.walletApiKeyPlaceholder}</p>
          )}
        </div>

        <Button
          size="sm"
          className="font-mono text-xs h-9 mt-3"
          onClick={() => void handleSave()}
          disabled={saving || value.length < 16}
        >
          {saving ? t.agentSettings.walletApiKeySaving : t.agentSettings.walletApiKeySave}
        </Button>
      </div>
    </div>
  )
}

// ─── Page ───────────────────────────────────────────────────────────────────

export default function AgentSettingsPage() {
  const t = useT()
  const [maintenanceMode, setMaintenanceMode] = useState<boolean | null>(null)
  const [loading, setLoading] = useState(true)
  const [toggling, setToggling] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await apiAgentGetDashboard()
      setMaintenanceMode((data as { maintenanceMode?: boolean }).maintenanceMode ?? false)
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.common.error)
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => { void load() }, [load])

  async function handleToggle() {
    if (maintenanceMode === null) return
    setToggling(true)
    setError(null)
    setSuccessMsg(null)
    try {
      const result = await apiAgentUpdateSettings(!maintenanceMode)
      setMaintenanceMode(result.maintenanceMode)
      setSuccessMsg(t.agentSettings.toggleSuccess)
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.agentSettings.toggleError)
    } finally {
      setToggling(false)
    }
  }

  return (
    <div className="p-4 md:p-6 max-w-lg">
      <div className="mb-5">
        <span className="font-mono text-xs font-bold uppercase tracking-wider text-foreground">
          {t.agentSettings.title}
        </span>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 border border-destructive/40 rounded-[var(--radius)] bg-card mb-4" role="alert">
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
          <p className="font-mono text-xs text-destructive">{error}</p>
        </div>
      )}

      {successMsg && (
        <div className="flex items-center gap-2 p-3 border border-success/40 rounded-[var(--radius)] bg-card mb-4">
          <span className="h-4 w-4 rounded-full bg-success/20 flex items-center justify-center shrink-0">
            <span className="h-2 w-2 rounded-full bg-success" />
          </span>
          <p className="font-mono text-xs text-success">{successMsg}</p>
        </div>
      )}

      <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden shadow-[var(--shadow-sm)]">
        <div className="px-4 py-2.5 bg-secondary flex items-center gap-2">
          <Wrench className="h-3.5 w-3.5 text-secondary-foreground/60" aria-hidden />
          <span className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
            {t.agentSettings.maintenanceMode}
          </span>
        </div>

        <div className="p-4">
          <p className="font-mono text-xs text-muted-foreground mb-4">
            {t.agentSettings.maintenanceDesc}
          </p>

          {loading ? (
            <div className="flex items-center gap-3">
              <Skeleton className="h-9 w-24" />
              <Skeleton className="h-4 w-32" />
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <div
                className={cn(
                  'flex items-center gap-2 px-3 py-1.5 rounded-[var(--radius-sm)] border text-xs font-mono font-semibold',
                  maintenanceMode
                    ? 'border-destructive/40 bg-destructive/10 text-destructive'
                    : 'border-success/40 bg-success/10 text-success',
                )}
              >
                <span className={cn('h-2 w-2 rounded-full', maintenanceMode ? 'bg-destructive' : 'bg-success')} />
                {maintenanceMode ? t.agentSettings.maintenanceOn : t.agentSettings.maintenanceOff}
              </div>

              <Button
                variant={maintenanceMode ? 'outline' : 'destructive'}
                size="sm"
                onClick={() => void handleToggle()}
                disabled={toggling}
                className="font-mono text-xs h-9"
              >
                {maintenanceMode ? t.agentSettings.maintenanceOff : t.agentSettings.maintenanceOn}
              </Button>
            </div>
          )}
        </div>
      </div>

      <WalletApiKeyCard />
    </div>
  )
}
